import { Col, Flex, Row, AutoComplete, AutoCompleteProps, Typography, Table, TableProps, FormInstance, Form, Button, Modal, Input } from 'antd';
import { useTranslation } from 'react-i18next';

import Icon from 'utils/icon';
import { isRTL } from 'core/services/language';
import { useState } from 'react';
import NotificationService from 'core/services/notification';
import { useDebouncedCallback } from 'use-debounce';
import { LoadingOutlined } from '@ant-design/icons';
import { onSearchDividendShareholder, onUpdateDividendShareholderIban } from '../services/dividends';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import dayjs from 'dayjs';
import { SearchDividendShareholderResponse } from '../models/dividends';
import FormItem from 'antd/es/form/FormItem';
import FormElement from 'components/formElement/formElement';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { TranslatedField } from 'core/models/common';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface DirectIbanComponentProps {
	form: FormInstance;
	eligibility_date: string;
	revenue_per_share: string;
	viewOnly?: boolean;
	disabled?: boolean;
}

function DirectIbanComponent({ form, eligibility_date, revenue_per_share, viewOnly, disabled }: DirectIbanComponentProps) {
	const { t } = useTranslation();

	const [IbanForm] = Form.useForm();

	const directTransferShareholdersWatcher = Form.useWatch('direct_transfer_excepted_shareholders', form) || [];
	const forbiddenShareholdersWatcher = Form.useWatch('forbidden_excepted_shareholders', form) || [];

	const [searchedShareholders, setSearchedShareholders] = useState<SearchDividendShareholderResponse['shareholders']>([]);
	const [searchedShareholdersOptions, setSearchedShareholdersOptions] = useState<AutoCompleteProps['options']>([]);
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');

	const [shareholderIdForIban, setShareholderIdForIban] = useState<number | null>(null);
	const [ibanModalOpen, setIbanModalOpen] = useState<boolean>(false);
	const [updatingIban, setUpdatingIban] = useState<boolean>(false);

	const columns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name: TranslatedField) => (isRTL() ? name.ar : name.en ?? name.ar),
		},
		{
			title: t('ACTIONS.PROFIT'),
			dataIndex: 'amount',
			key: 'amount',
		},
		{
			title: t('ACTIONS.IBAN'),
			dataIndex: 'iban',
			key: 'iban',
			render: (iban: string, record) =>
				iban ? (
					iban
				) : (
					<Button
						color="red"
						danger
						size="middle"
						style={{ background: '#fff' }}
						onClick={() => {
							setShareholderIdForIban(record.id);
							setIbanModalOpen(true);
						}}>
						{t('ACTIONS.ENTER_IBAN')}
					</Button>
				),
		},
		{
			title: '',
			key: 'actions',
			render: (_, record) => <ListButton type="delete" onClick={() => onDeleteShareholder(record.id)} />,
		},
	];

	const searchShareholderHandler = useDebouncedCallback(async (text: string) => {
		try {
			setIsSearching(true);

			const response = await onSearchDividendShareholder(dayjs(eligibility_date).format('YYYY-MM-DD'), revenue_per_share, text);

			setSearchedShareholders(response.shareholders);

			setSearchedShareholdersOptions(
				response.shareholders
					.filter(
						(shareholder) =>
							!directTransferShareholdersWatcher.some((direct: { id: number }) => direct.id === shareholder.id) &&
							!forbiddenShareholdersWatcher.some((forbidden: { id: number }) => forbidden.id === shareholder.id),
					)
					.map((shareholder) => ({
						label: (
							<Flex align="center" gap={10}>
								<Icon color="var(--primary)" name="profile" />
								<p className="mb-0 py-1" style={{ fontSize: 15 }}>{`${
									isRTL() ? shareholder.name.ar : shareholder.name.en ?? shareholder.name.ar
								}`}</p>
							</Flex>
						),
						value: `${shareholder.id}`,
					})),
			);

			setIsSearching(false);
		} catch (error) {
			setIsSearching(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	}, 800);

	const selectShareholderHandler = (shareholderId: string) => {
		const shareholder = searchedShareholders.find((item) => item.id == Number(shareholderId));

		if (shareholder) {
			form.setFieldValue('direct_transfer_excepted_shareholders', [
				...directTransferShareholdersWatcher,
				{
					id: shareholder.id,
					name: shareholder.name,
					amount: shareholder.amount,
					iban: shareholder.iban,
				},
			]);
		}

		setSearchValue('');
		setSearchedShareholders([]);
		setSearchedShareholdersOptions([]);
	};

	const onDeleteShareholder = (shareholderId: number) => {
		const updatedShareholders = directTransferShareholdersWatcher.filter((item: { id: number }) => item.id !== shareholderId);
		form.setFieldValue('direct_transfer_excepted_shareholders', updatedShareholders);
	};

	const updateIbanHandler = async (values: { iban: string }) => {
		try {
			setUpdatingIban(true);

			await onUpdateDividendShareholderIban(dayjs(eligibility_date).format('YYYY-MM-DD'), shareholderIdForIban!.toString(), values.iban);

			const updatedShareholders = directTransferShareholdersWatcher.map((item: { id: number; iban: string }) => {
				if (item.id === shareholderIdForIban) {
					return { ...item, iban: values.iban };
				}
				return item;
			});
			form.setFieldValue('direct_transfer_excepted_shareholders', updatedShareholders);
			setUpdatingIban(false);
			setShareholderIdForIban(null);
			setIbanModalOpen(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.IBAN_UPDATED_SUCCESSFULLY'));
		} catch (error: any) {
			setUpdatingIban(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));

			if (error.status === 422) {
				mapResponseErrors(error, IbanForm);
			}
		}
	};

	return (
		<>
			<Row gutter={[16, 16]} className="my-4">
				<Col span={24} md={8}>
					<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'inline-block' }}>
						{t('ACTIONS.SHAREHOLDER_DIRECT_IBAN_TRANSFER')}
					</Typography.Text>

					<Form.Item hidden noStyle name="direct_transfer_excepted_shareholders" />
					<AutoComplete
						backfill
						value={searchValue}
						onChange={setSearchValue}
						style={{ width: '100%', height: 'auto' }}
						placeholder={t('ACTIONS.SEARCH_BY_NATIONAL_ID_OR_NAME')}
						prefix={<Icon name="search" style={{ marginInlineEnd: 10, marginTop: -3 }} />}
						options={searchedShareholdersOptions}
						onSelect={selectShareholderHandler}
						onSearch={searchShareholderHandler}
						disabled={viewOnly || disabled || !eligibility_date || !revenue_per_share}
						notFoundContent={
							isSearching ? (
								<span>
									<LoadingOutlined style={{ marginInlineEnd: 10, color: 'var(--primary)' }} /> {t('COMMON.SEARCHING')}
								</span>
							) : (
								t('COMMON.NO_DATA_FOUND')
							)
						}
					/>
				</Col>
				<Col span={24} md={16}>
					<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'inline-block' }}>{t('ACTIONS.DIRECT_TRANSFER_TO_IBAN')}</Typography.Text>

					<Table
						columns={columns}
						dataSource={directTransferShareholdersWatcher}
						bordered
						rowKey="id"
						pagination={false}
						locale={{ emptyText: <NoData message=" " /> }}
					/>
				</Col>
			</Row>

			<Modal
				open={ibanModalOpen}
				title={<h6>{t('ACTIONS.ENTER_IBAN')}</h6>}
				onCancel={() => {
					setShareholderIdForIban(null);
					setIbanModalOpen(false);
				}}
				footer={[
					<Button
						key="cancel"
						danger
						size="middle"
						onClick={() => {
							setShareholderIdForIban(null);
							setIbanModalOpen(false);
						}}>
						{t('COMMON.CANCEL')}
					</Button>,
					<SuspendedTooltip key="confirm">
						<Button color="primary" type="primary" key="confirm" size="middle" onClick={() => IbanForm.submit()} loading={updatingIban}>
							{t('COMMON.UPDATE')}
						</Button>
					</SuspendedTooltip>,
				]}
				destroyOnClose>
				<Form form={IbanForm} onFinish={updateIbanHandler} className="my-3">
					<FormElement
						fullWidth
						type="text"
						name="iban"
						rules={[
							{ required: true, message: t('COMMON.FIELD_REQUIRED') },
							{ len: 15, message: t('ACTIONS.IBAN_VALIDATION') },
						]}
					/>
				</Form>
			</Modal>
		</>
	);
}

export default DirectIbanComponent;
